export const normalizeDashboardStoryType = (story) => {
  if (story && typeof story === 'object') {
    if (story.layout_mode === 'spread') return 'spread_story';
    return normalizeDashboardStoryType(story.comfy_flow || 'dynamic_story');
  }
  return story === 'prompt_story' ? 'dynamic_story' : story;
};

export const getDefaultMontageProfileKey = (rawStoryType) => {
  const storyType = normalizeDashboardStoryType(rawStoryType);
  if (storyType === 'spread_story') return 'spread-story-v1';
  return storyType === 'legacy_static' ? 'legacy-static-v1' : 'dynamic-story-v1';
};

export const getDefaultMontageProfileOutputs = (storyType) => (
  normalizeDashboardStoryType(storyType) === 'spread_story' ? ['readable'] : ['montage']
);

const LANE_FIELDS = {
  hard_cover: 'montage_profile_hard_cover',
  booklet: 'montage_profile_booklet',
};

const DYNAMIC_LANE_SELECTORS = [
  {
    assignment: 'booklet',
    field: LANE_FIELDS.booklet,
    label: 'Booklet (No Premium)',
    helpText: 'Used for dynamic orders without Premium. Express changes delivery speed but keeps this booklet montage.',
    requiredOutputs: ['booklet'],
  },
  {
    assignment: 'hard_cover',
    field: LANE_FIELDS.hard_cover,
    label: 'Hard Cover (Premium)',
    helpText: 'Used for all dynamic Premium orders, including Express.',
    requiredOutputs: ['montage', 'cover'],
  },
];

const LEGACY_LANE_SELECTORS = [
  {
    assignment: 'hard_cover',
    field: LANE_FIELDS.hard_cover,
    label: 'Hard Cover 20×20',
    helpText: 'Used for all legacy orders, including Express. This profile also supplies the readable print file.',
    requiredOutputs: ['montage', 'readable', 'cover'],
  },
];

export const getMontageLaneSelectors = (rawStoryType) => {
  const storyType = normalizeDashboardStoryType(rawStoryType);
  if (storyType === 'legacy_static') return LEGACY_LANE_SELECTORS;
  return storyType === 'spread_story'
    ? DYNAMIC_LANE_SELECTORS.map(lane => lane.assignment === 'hard_cover'
      ? { ...lane, requiredOutputs: ['readable', 'cover'] } : lane)
    : DYNAMIC_LANE_SELECTORS;
};

export const buildMontageProfileAssignments = (formData, existing = {}, profiles = []) => {
  const storyType = normalizeDashboardStoryType(formData);
  const defaultKey = formData.montage_profile_key || getDefaultMontageProfileKey(storyType);
  const defaultProfile = profiles.find((p) => p.key === defaultKey);
  const fallbackKey = getDefaultMontageProfileKey(storyType);

  const assignments = {
    ...existing,
    default: defaultKey,
  };
  delete assignments.booklet_express;
  delete assignments.pristol_express;
  if (storyType === 'legacy_static') {
    delete assignments.booklet;
  }

  const laneSelectors = getMontageLaneSelectors(storyType);
  laneSelectors.forEach(({ assignment, field, requiredOutputs }) => {
    if (formData[field]) {
      assignments[assignment] = formData[field];
    } else {
      const satisfiesDefault = !defaultProfile || requiredOutputs.every((out) => Boolean(defaultProfile.outputs?.[out]));
      if (satisfiesDefault) {
        delete assignments[assignment];
      } else {
        assignments[assignment] = fallbackKey;
      }
    }
  });

  return assignments;
};

export const getCompatibleMontageProfiles = (
  profiles,
  rawStoryType,
  currentKey = '',
  requiredOutputs = []
) => {
  const storyType = normalizeDashboardStoryType(rawStoryType);
  return (profiles || []).filter((profile) => (
    Array.isArray(profile.story_types)
    && profile.story_types.includes(storyType)
    && (profile.active !== false || profile.key === currentKey)
    && requiredOutputs.every((output) => Boolean(profile.outputs?.[output]))
  ));
};

export const buildMontageProfileOptions = (
  profiles,
  storyType,
  currentKey = '',
  requiredOutputs = []
) => {
  const compatible = getCompatibleMontageProfiles(
    profiles,
    storyType,
    currentKey,
    requiredOutputs
  );
  const options = compatible.map((profile) => ({
    label: `${profile.name} (${profile.key})${profile.active === false ? ' - inactive' : ''}`,
    value: profile.key,
  }));
  if (currentKey && !options.some((option) => option.value === currentKey)) {
    options.unshift({ label: `${currentKey} (unavailable)`, value: currentKey });
  }
  return options;
};

export const buildMontageLaneOptions = (
  profiles,
  storyType,
  defaultKey,
  currentKey = '',
  requiredOutputs = []
) => [
  {
    label: `Use default profile (${defaultKey || getDefaultMontageProfileKey(storyType)})`,
    value: '',
  },
  ...buildMontageProfileOptions(
    profiles,
    storyType,
    currentKey,
    requiredOutputs
  ),
];

export const resolveMontageProfileKey = (
  profiles,
  storyType,
  currentKey = '',
  requiredOutputs = []
) => {
  const compatible = getCompatibleMontageProfiles(
    profiles,
    storyType,
    currentKey,
    requiredOutputs
  );
  if (currentKey && compatible.some((profile) => profile.key === currentKey)) {
    return currentKey;
  }
  const builtInKey = getDefaultMontageProfileKey(storyType);
  if (compatible.some((profile) => profile.key === builtInKey)) {
    return builtInKey;
  }
  return compatible[0]?.key || builtInKey;
};
